import { useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useMyCommsCountersQuery } from 'core/graphql';
import { userStateVar } from 'core/state/userState';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import {
  canReadCommsCounters,
  MY_COMMS_COUNTERS_ROLES,
} from './commsCounterQueries';
import { BADGE_POLL_INTERVAL_MS } from './useGroupCommsIndex';

export { MY_COMMS_COUNTERS_ROLES };

export interface IMyCommsCounters {
  mailUnread: number;
  slackUnread: number;
  unhandled: number;
  overdueTasks: number;
}

export interface IUseMyCommsCounters {
  /** `null` — роли счётчики не положены или ответа ещё нет. */
  counters: IMyCommsCounters | null;
  loading: boolean;
}

/**
 * ТЗ 0096 — счётчики меню и шапки Таск-трекера: непрочитанные письма и
 * Slack, «ждёт ответа», просроченные задачи.
 *
 * `cache-and-network` + опрос раз в 60 с ТОЛЬКО при видимой вкладке (как
 * значки Коммуникатора, ТЗ 0008). Сразу после чтения/обработки счётчик
 * перечитывается документом (`commsCounterRefetch()`, только ролям с
 * правом — перепроверка F47) — меню не ждёт следующего опроса.
 */
export const useMyCommsCounters = (): IUseMyCommsCounters => {
  const roles = useReactiveVar(userStateVar).me?.roles ?? [];
  const allowed = canReadCommsCounters(roles);

  const { data, loading, startPolling, stopPolling } = useMyCommsCountersQuery({
    skip: !allowed,
    fetchPolicy: 'cache-and-network',
  });
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    BADGE_POLL_INTERVAL_MS,
    allowed,
  );

  const raw = data?.myCommsCounters;
  const counters = useMemo<IMyCommsCounters | null>(
    () =>
      allowed && raw
        ? {
            mailUnread: raw.mailUnread,
            slackUnread: raw.slackUnread,
            unhandled: raw.unhandled,
            overdueTasks: raw.overdueTasks,
          }
        : null,
    [allowed, raw],
  );

  return { counters, loading: allowed && loading && !raw };
};
